import { useCallback, useState } from 'react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  // Input,
} from '@/components';
import {
  Button,
  CurrencyFormat,
  Field,
  Modal,
  Spinner,
  SuccessStatus,
} from '@/components/generic';
import { formatUnitsToAmount } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { bitcoinChain, useChainStore, useMagic } from '@package/smart-wallet';
import {
  useGetTokenBalances,
  useWithdrawBalance,
} from '@package/smart-wallet/hooks';
import { isAllowedAddress } from '@package/smart-wallet/utils';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

/* Types & Schemas */
export const WithdrawBalanceFormSchema = z.object({
  token: z
    .string({
      message: 'Token is required',
    })
    .min(1, { message: 'Token selection is required' }),
  amount: z
    .string({
      message: 'Amount is required',
    })
    .min(1, { message: 'Amount cannot be empty' })
    .refine(
      (amount) => {
        return !isNaN(Number(amount));
      },
      { message: 'Amount must be a number' },
    ),
  address: z
    .string({
      message: 'Address is required',
    })
    .min(1, { message: 'Address cannot be empty' })
    .refine(
      (address) => {
        return isAllowedAddress(address);
      },
      {
        message: 'Invalid wallet address format',
      },
    ),
});

export type WithdrawBalanceFormType = z.infer<typeof WithdrawBalanceFormSchema>;

export interface WithdrawModalProps {
  open: boolean;
  onClose: () => void;
}

export const WithdrawModal = ({ open, onClose }: WithdrawModalProps) => {
  const chainId = useChainStore((state) => state.chainId);
  const { bitcoin } = useMagic();
  const { handleWithdrawBalance } = useWithdrawBalance();
  const { isLoading, tokenSymbol, tokenAmount, tokens } = useGetTokenBalances({
    allTokens: true,
  });
  const isBtcChain = chainId === bitcoinChain.id;
  const [showSuccess, setShowSuccess] = useState(false);
  const [txHash, setTxHash] = useState<string>('');
  const [withdrawData, setWithdrawData] = useState<{
    amount: string;
    token: string;
    address: string;
  } | null>(null);

  const form = useForm<WithdrawBalanceFormType>({
    resolver: zodResolver(WithdrawBalanceFormSchema),
    defaultValues: {
      token: '',
      amount: '',
      address: '',
    },
  });

  /* Callbacks */
  const handleClose = useCallback(() => {
    form.reset();
    setShowSuccess(false);
    setTxHash('');
    setWithdrawData(null);
    onClose();
  }, [form, onClose]);

  const onSubmit = useCallback(
    async (data: WithdrawBalanceFormType) => {
      try {
        const { token, amount, address } = data;
        console.log('=== Withdraw Data ===', { token, amount, address });

        let transactionHash: string;

        if (isBtcChain) {
          // Bitcoin withdrawal
          if (!bitcoin) {
            throw new Error('Bitcoin provider not initialized');
          }

          // Convert BTC to satoshis (1 BTC = 100,000,000 satoshis)
          const amountInSatoshis = Math.floor(Number(amount) * 100000000);

          // Send Bitcoin transaction (UTXO selection is handled automatically)
          transactionHash = await bitcoin.send({
            recipientAddress: address,
            amount: amountInSatoshis,
          });

          console.log('=== Bitcoin txHash ===', transactionHash);
        } else {
          // EVM withdrawal
          transactionHash = await handleWithdrawBalance(token, amount, address);

          if (!transactionHash) {
            throw new Error('Withdraw transaction failed');
          }

          console.log('=== EVM txHash ===', transactionHash);
        }

        // Store transaction data and show success status
        setTxHash(transactionHash);
        setWithdrawData({ amount, token, address });
        setShowSuccess(true);
        form.reset();
      } catch (error) {
        console.error('Error submitting form:', error);
        toast.error(
          error instanceof Error
            ? error.message
            : 'Failed to process withdrawal. Please try again.',
        );
      }
    },
    [form, handleWithdrawBalance, isBtcChain, bitcoin],
  );

  const handleTokenSelect = (tokenSymbol: string) => {
    form.setValue('token', tokenSymbol);
  };

  const handleMaxAmount = useCallback(() => {
    const selectedToken = form.getValues('token');

    if (!selectedToken) {
      toast.error('Please select a token first');
      return;
    }

    if (isBtcChain) {
      // For Bitcoin: use full balance (fee will be deducted on send)
      const balanceInSatoshis = Number(tokenAmount || '0');
      const maxAmountBTC = (balanceInSatoshis / 100000000).toFixed(8);
      form.setValue('amount', maxAmountBTC);
    } else {
      // For EVM tokens
      const token = tokens.find((t) => t.symbol === selectedToken);
      if (token) {
        const maxAmount = formatUnitsToAmount({
          string: token.amount,
          decimalToken: token.decimals,
        });
        form.setValue('amount', maxAmount);
      }
    }
  }, [form, isBtcChain, tokenAmount, tokens]);
  return (
    <Modal open={open} onClose={handleClose}>
      {showSuccess ? (
        <SuccessStatus
          title='Withdrawal Successful'
          content={
            <>
              Successfully sent{' '}
              <strong>
                {withdrawData?.amount} {withdrawData?.token}
              </strong>{' '}
              to{' '}
              <span className='break-all'>
                {withdrawData?.address.slice(0, 10)}...
                {withdrawData?.address.slice(-8)}
              </span>
            </>
          }
          txHash={txHash}
          onClose={handleClose}
        />
      ) : (
        <div className='text-center'>
          <h2 className='text-h5 mb-6 text-t-primary '>Withdraw</h2>

          <Form {...form}>
            <form
              className='w-full flex flex-col gap-2 bg-b-surface1'
              onSubmit={form.handleSubmit(onSubmit)}
            >
              {/* Token Selector */}
              <FormField
                control={form.control}
                name='token'
                render={({ field }) => (
                  <FormItem>
                    <div className='text-start mb-2'>
                      <label className='text-sm font-medium text-t-secondary'>
                        Select Token
                      </label>
                    </div>
                    <FormControl>
                      <div className='flex flex-col gap-2'>
                        {isLoading ? (
                          <div className='flex justify-center py-4'>
                            <Spinner className='bg-transparent' />
                          </div>
                        ) : isBtcChain ? (
                          <div
                            onClick={() =>
                              handleTokenSelect(tokenSymbol || 'BTC')
                            }
                            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${field.value === tokenSymbol
                              ? 'bg-b-accent border-2 border-b-accent'
                              : 'bg-b-surface2 hover:bg-b-surface3 border-2 border-transparent'
                              }`}
                          >
                            <div className='flex items-center gap-3'>
                              <div className='text-start'>
                                <div className='font-medium text-t-primary'>
                                  {tokenSymbol}
                                </div>
                              </div>
                            </div>
                            <div className='text-right'>
                              <CurrencyFormat
                                className='text-sm font-medium text-t-primary'
                                postfix={tokenSymbol}
                                currency=''
                                decimalAmount={8}
                                value={Number(
                                  formatUnitsToAmount({
                                    string: tokenAmount || '0',
                                    decimalToken: 8,
                                  }),
                                )}
                              />
                            </div>
                          </div>
                        ) : (
                          tokens.map((token) => {
                            const isSelected = field.value === token.symbol;
                            return (
                              <div
                                key={token.symbol}
                                onClick={() => handleTokenSelect(token.symbol)}
                                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${isSelected
                                  ? 'bg-b-accent border-2 border-b-accent'
                                  : 'bg-b-surface2 hover:bg-b-surface3 border-2 border-transparent'
                                  }`}
                              >
                                <div className='flex items-center gap-3'>
                                  {token.icon && (
                                    <img
                                      src={token.icon}
                                      alt={token.symbol}
                                      className='w-8 h-8 rounded-full'
                                    />
                                  )}
                                  <div className='text-start'>
                                    <div className='font-medium text-t-primary'>
                                      {token.symbol}
                                    </div>
                                    {token.isPrimary && (
                                      <span className='text-xs text-t-tertiary'>
                                        Primary
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className='text-right'>
                                  <CurrencyFormat
                                    className='text-sm font-medium text-t-primary'
                                    postfix={token.symbol}
                                    currency=''
                                    decimalAmount={
                                      token.symbol.includes('USD')
                                        ? 4
                                        : token.decimals
                                    }
                                    value={Number(
                                      formatUnitsToAmount({
                                        string: token.amount,
                                        decimalToken: token.decimals,
                                      }),
                                    )}
                                  />
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </FormControl>
                    <FormMessage className='text-start text-red-400' />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='amount'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className='relative'>
                        <Field
                          label='Amount'
                          placeholder='0.00'
                          {...field}
                          type='text'
                          pattern='^\d*\.?\d*$'
                          inputMode='decimal'
                        />
                        <button
                          type='button'
                          onClick={handleMaxAmount}
                          className='absolute right-3 top-[38px] px-3 py-1 text-xs font-semibold bg-b-accent text-t-primary rounded hover:bg-b-accent/80 transition-colors'
                        >
                          MAX
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage className='text-start text-red-400' />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='address'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Field
                        label='Wallet Address'
                        tooltip={
                          isBtcChain
                            ? 'Please enter a valid Bitcoin wallet address'
                            : 'Please enter a valid EVM wallet address'
                        }
                        {...field}
                        placeholder={
                          isBtcChain
                            ? 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh'
                            : '0x0000000000000000000000000000000000000000'
                        }
                      />
                    </FormControl>
                    <FormMessage className='text-start text-red-400' />
                  </FormItem>
                )}
              />
              <div className='mb-8'>
                <p className='text-body-2 text-t-secondary mb-4'>
                  You can withdraw your assets to any wallet on the same
                  network.
                </p>
              </div>
              <Button
                className='w-full cursor-pointer uppercase'
                type='submit'
                isBlack
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? <Spinner /> : 'withdraw'}
              </Button>
            </form>
          </Form>
        </div>
      )}
    </Modal>
  );
};
